import React, { useEffect, useMemo } from 'react';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getLiningColourOptions } from '../../../helpers/getLiningColourOptions';
import { getLiningHexColour } from '../../../helpers/getHexColorCode';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { throwError } from '../../../helpers/throwError';
import { ProductBuilderMachineEvent } from '../../../types';
import { Caption } from '../../Common/Caption';
import { Heading } from '../../Common/Heading';
import { Selector, SelectorColour, SelectorItem } from '../../Common/Selector';
import { SectionContent } from './SectionContent';
import { getBaseProduct } from '../../../helpers/getBaseProduct';

type OnClick = (
  liningColour: string,
  variantId: number,
  price: number,
  send: (event: ProductBuilderMachineEvent) => void,
) => () => void;

const onClick: OnClick = (liningColour, variantId, price, send) => () =>
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      liningColour,
      lining_colour_variant_id: variantId,
      lining_colour_price: price,
    } as any,
  });

export const LiningColour = () => {
  console.log('🎨 LiningColour component rendered!');
  const [state, send] = useService(productBuilderMachine);
  const {
    custom: { liningColour, measurements, lining, lining_colour_variant_id, lining_colour_price },
    curtainblock,
    curtainsemi,
  } = state.context;

  // Helper function to find variant price by variant ID
  const findVariantPrice = (variantId: number): number => {
    if (!variantId || !measurements || !lining) return 0;
    
    // Determine which lining product to use based on lining selection
    let liningProduct;
    if (lining === 'Blockout' || lining === 'Block-out') {
      liningProduct = curtainblock && Array.isArray(curtainblock) && curtainblock.length > 0 
        ? curtainblock[0] 
        : null;
    } else if (lining === 'In-between/Dim Out' || lining === 'Semi-Transparent' || lining === 'Semi-transparent') {
      liningProduct = curtainsemi && Array.isArray(curtainsemi) && curtainsemi.length > 0 
        ? curtainsemi[0] 
        : null;
    } else {
      return 0;
    }
    
    if (liningProduct?.variants) {
      const variant = liningProduct.variants.find((v: any) => v.id === variantId);
      if (variant) return variant.price;
    }
    
    return 0;
  };

  // Get dynamic lining colour options based on measurements and lining type
  const dynamicLiningOptions = getLiningColourOptions(state.context);

  console.log('LiningColour component debug:', {
    liningColour,
    measurements,
    lining,
    dynamicLiningOptionsLength: dynamicLiningOptions.length,
    dynamicLiningOptions,
  });
  const baseProduct = getBaseProduct(state.context);


  // Fallback to static options if dynamic options are not available
  const fallbackOptions = baseProduct
    ? baseProduct.metafields.lining_colour || []
    : [];
  const hasDynamicOptions = useMemo(() => dynamicLiningOptions.length > 0, [
    dynamicLiningOptions.length,
  ]);

  // Set default to first option if no selection - only run when liningColour changes or options become available
  // Do NOT set price automatically - only set when user manually selects
  useEffect(() => {
    // Only recalculate price when user is on this step (not automatically)
    // This ensures addon prices are only added when user reaches this step
    const onLiningColourStep = isCurrentState('liningColour', state);
    
    if (
      onLiningColourStep &&
      lining_colour_variant_id &&
      (!lining_colour_price || lining_colour_price === 0) &&
      measurements && // Only recalculate if measurements are available
      lining // And lining type is selected
    ) {
      const recalculatedPrice = findVariantPrice(lining_colour_variant_id);
      if (recalculatedPrice > 0) {
        send({
          type: 'UPDATE_CUSTOM',
          custom: {
            lining_colour_price: recalculatedPrice,
          } as any,
        });
        return; // Exit early after recalculating price
      }
    }

    // Only set default if we don't have a selection and have options available
    if (!liningColour && (hasDynamicOptions || fallbackOptions.length > 0)) {
      if (hasDynamicOptions && dynamicLiningOptions.length > 0) {
        const firstOption = dynamicLiningOptions[0];
        send({
          type: 'UPDATE_CUSTOM',
          custom: {
            liningColour: firstOption.id,
            lining_colour_variant_id: firstOption.variantId,
            // Do NOT set price automatically - user must manually select to add price
            // lining_colour_price: firstOption.price,
          } as any,
        });
      } else if (fallbackOptions.length > 0) {
        // Fallback to static option
        send({
          type: 'UPDATE_CUSTOM',
          custom: {
            liningColour: fallbackOptions[0].toLowerCase(),
          } as any,
        });
      }
    }
  }, [liningColour, hasDynamicOptions, lining_colour_variant_id, lining_colour_price, measurements, lining, send]); // Only depend on stable values to avoid callback hell

  // Don't throw error if we have fallback options
  if (!hasDynamicOptions && fallbackOptions.length === 0) {
    if (isCurrentState('liningColour', state)) {
      throwError(state);
    }
  }

  const selectedOption = dynamicLiningOptions.find(
    option => option.id === liningColour,
  );
  const displayOptions = hasDynamicOptions
    ? dynamicLiningOptions
    : fallbackOptions.map(colour => ({
        id: colour.toLowerCase(),
        title: colour,
        price: 0,
        variantId: 0,
        width: 0,
      }));

  return (
    <SectionContent>
      <Heading>Lining Colour</Heading>
      <Selector isSlider={true} isColorPicker={true}>
        {displayOptions.map(option => {
          return (
            <SelectorColour
              isSelected={option.id === liningColour}
              onClick={
                hasDynamicOptions
                  ? onClick(option.id, option.variantId, option.price, send)
                  : onClick(option.id, 0, 0, send)
              }
              key={option.id}
              colourName={option.title}
              colourCode={getLiningHexColour(option.id)}
              colourSwatch={''}
            />
          );
        })}
      </Selector>
      <Caption>{selectedOption ? selectedOption.title : liningColour}</Caption>
      {/* {selectedOption && selectedOption.price > 0 && (
        <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: 'bold' }}>
          Price: ${(selectedOption.price / 100).toFixed(2)}
        </div>
      )} */}
    </SectionContent>
  );
};
